import { html, nothing } from "lit";
import type { WorkspaceTreeNode } from "../controllers/chat-workspace.ts";

export type FileContextState = { open: boolean; query: string };
export type FolderContextState = { open: boolean; query: string };

/** `@file:` mention at end of draft (Cursor-style file context). */
const FILE_CONTEXT_RE = /@file:([^\s]*)$/;

/** `@folder:` or `@dir:` mention at end of draft (folder context). */
const FOLDER_CONTEXT_RE = /@(?:folder|dir):([^\s]*)$/;

export function extractFileContextQuery(draft: string): FileContextState {
  const match = FILE_CONTEXT_RE.exec(draft);
  if (!match) {
    return { open: false, query: "" };
  }
  return { open: true, query: match[1] ?? "" };
}

export function formatFileContextToken(path: string): string {
  const trimmed = path.trim().replace(/\\/g, "/");
  return `@file:${trimmed} `;
}

export function extractFolderContextQuery(draft: string): FolderContextState {
  const match = FOLDER_CONTEXT_RE.exec(draft);
  if (!match) {
    return { open: false, query: "" };
  }
  return { open: true, query: match[1] ?? "" };
}

export function formatFolderContextToken(path: string): string {
  const trimmed = path.trim().replace(/\\/g, "/");
  return `@folder:${trimmed} `;
}

function flattenWorkspacePaths(
  nodes: WorkspaceTreeNode[],
): Array<{ path: string; name: string; kind: "file" | "dir" }> {
  const out: Array<{ path: string; name: string; kind: "file" | "dir" }> = [];
  for (const node of nodes) {
    const relPath = node.path?.trim() || node.name;
    out.push({ path: relPath, name: node.name, kind: node.kind });
    if (node.kind === "dir" && node.children?.length) {
      out.push(...flattenWorkspacePaths(node.children));
    }
  }
  return out;
}

export function filterWorkspacePaths(
  tree: WorkspaceTreeNode[],
  query: string,
  max = 12,
): Array<{ path: string; name: string; kind: "file" | "dir" }> {
  const q = query.trim().toLowerCase();
  const flat = flattenWorkspacePaths(tree).filter((e) => e.kind === "file" || q);
  const filtered = q
    ? flat.filter((e) => e.path.toLowerCase().includes(q) || e.name.toLowerCase().includes(q))
    : flat.filter((e) => e.kind === "file").slice(-max);
  const exactName = filtered.filter((e) => e.name.toLowerCase() === q);
  const rest = filtered.filter((e) => e.name.toLowerCase() !== q);
  const ranked = [
    ...exactName.toSorted((a, b) => a.path.split("/").length - b.path.split("/").length),
    ...rest,
  ];
  return ranked.slice(0, max);
}

export function filterFolderPaths(
  tree: WorkspaceTreeNode[],
  query: string,
  max = 12,
): Array<{ path: string; name: string; kind: "dir" }> {
  const q = query.trim().toLowerCase();
  const flat = flattenWorkspacePaths(tree).filter(
    (e): e is { path: string; name: string; kind: "dir" } => e.kind === "dir",
  );
  const filtered = q
    ? flat.filter((e) => e.path.toLowerCase().includes(q) || e.name.toLowerCase().includes(q))
    : flat.slice(-max);
  return filtered.slice(0, max);
}

export type FileContextPickerProps = {
  tree: WorkspaceTreeNode[];
  query: string;
  activeIndex: number;
  onSelect: (path: string) => void;
  onHoverIndex: (index: number) => void;
};

export function renderFileContextPicker(props: FileContextPickerProps) {
  const matches = filterWorkspacePaths(props.tree, props.query);
  if (matches.length === 0) {
    return nothing;
  }
  const index = Math.max(0, Math.min(props.activeIndex, matches.length - 1));
  return html`
    <div class="chat-file-context" role="listbox" aria-label="File context">
      ${matches.map(
        (entry, i) => html`
          <button
            type="button"
            class="chat-file-context__item ${i === index ? "chat-file-context__item--active" : ""}"
            role="option"
            aria-selected=${i === index}
            @click=${() => props.onSelect(entry.path)}
            @mouseenter=${() => props.onHoverIndex(i)}
          >
            <span class="chat-file-context__kind muted">${entry.kind === "dir" ? "dir" : "file"}</span>
            <span class="chat-file-context__path mono">${entry.path}</span>
          </button>
        `,
      )}
    </div>
  `;
}

export type FolderContextPickerProps = {
  tree: WorkspaceTreeNode[];
  query: string;
  activeIndex: number;
  onSelect: (path: string) => void;
  onHoverIndex: (index: number) => void;
};

export function renderFolderContextPicker(props: FolderContextPickerProps) {
  const matches = filterFolderPaths(props.tree, props.query);
  if (matches.length === 0) {
    return nothing;
  }
  const index = Math.max(0, Math.min(props.activeIndex, matches.length - 1));
  return html`
    <div class="chat-file-context chat-folder-context" role="listbox" aria-label="Folder context">
      <div class="chat-file-context__header">
        <span class="muted">📁 Select a folder</span>
      </div>
      ${matches.map(
        (entry, i) => html`
          <button
            type="button"
            class="chat-file-context__item chat-file-context__item--folder ${i === index ? "chat-file-context__item--active" : ""}"
            role="option"
            aria-selected=${i === index}
            @click=${() => props.onSelect(entry.path)}
            @mouseenter=${() => props.onHoverIndex(i)}
          >
            <span class="chat-file-context__kind muted">📁</span>
            <span class="chat-file-context__path mono">${entry.path}</span>
          </button>
        `,
      )}
    </div>
  `;
}
